import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { createColumnHelper, type ColumnDef, type SortingState } from "@tanstack/react-table";
import { type components } from "@njbsoft/types";
import { useCatalogShutoffValves } from "@/hooks/use-catalog-shutoff-valves";
import {
  Badge,
  Breadcrumbs,
  DataTable,
  EmptyState,
  PageHeader,
  SkeletonTable,
  catalogStatusVariant,
} from "@/components/ui";

type CatalogShutoffValve = components["schemas"]["CatalogShutoffValve"];

export const Route = createFileRoute("/_authenticated/catalog/shutoff-valves")({
  component: CatalogShutoffValvesPage,
});

const columnHelper = createColumnHelper<CatalogShutoffValve>();

function CatalogShutoffValvesPage() {
  const { t } = useTranslation(["catalog", "common", "nav"]);

  // Lookup table is ~167 rows — fetch the full list in one page (server's
  // 200-row clamp is fine for this dataset). Sort + filter run client-side
  // via TanStack Table's getSortedRowModel + a simple text filter.
  const { data, isLoading, isError, error } = useCatalogShutoffValves({ limit: 200 });
  const items = useMemo(() => data?.items ?? [], [data]);

  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "code", desc: false }]);

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (it) =>
        it.code.toLowerCase().includes(needle) || it.description.toLowerCase().includes(needle),
    );
  }, [items, search]);

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("code", {
          id: "code",
          enableSorting: true,
          header: () => t("lookup.fields.code"),
          cell: (ctx) => <span className="font-mono font-medium">{ctx.getValue()}</span>,
        }),
        columnHelper.accessor("description", {
          id: "description",
          enableSorting: true,
          header: () => t("lookup.fields.description"),
          cell: (ctx) => ctx.getValue(),
        }),
        columnHelper.accessor("status", {
          id: "status",
          enableSorting: true,
          header: () => t("filters.status"),
          cell: (ctx) => {
            const s = ctx.getValue();
            return <Badge variant={catalogStatusVariant(s)}>{t(`status.${s}`)}</Badge>;
          },
        }),
        // `columnHelper.accessor` returns `ColumnDef<TData, TValue>` where
        // `TValue` is narrowed per-column. An array of heterogeneous
        // accessor results does not unify to `ColumnDef<TData, unknown>[]`
        // via inference, so we widen here. Canonical TanStack Table v8.
      ] as ColumnDef<CatalogShutoffValve, unknown>[],
    [t],
  );

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          { label: t("catalog.group", { ns: "nav" }) },
          { label: t("page.shutoffValvesTitle") },
        ]}
      />
      <PageHeader title={t("page.shutoffValvesTitle")} />

      <div className="mt-6">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("filters.searchShutoffValves")}
          className="w-full max-w-sm rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-700 dark:bg-gray-700 dark:text-white"
        />
      </div>

      {isError && (
        <div className="mt-6 rounded-md bg-danger/10 p-4 text-danger">
          {error instanceof Error ? error.message : t("errors.loadFailed")}
        </div>
      )}

      {isLoading ? (
        <div className="mt-6">
          <SkeletonTable columns={3} rows={5} />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="mt-6">
          <EmptyState title={t("page.shutoffValvesTitle")} description={t("empty.shutoffValves")} />
        </div>
      ) : (
        <div className="mt-6">
          <DataTable
            columns={columns}
            data={filteredItems}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting={false}
          />
        </div>
      )}
    </div>
  );
}
